import React, { createContext, useContext, useState, useEffect } from 'react'

type Theme = 'light' | 'dark'

interface IThemeContextProps {
  theme: Theme
  toggleTheme: () => void
}

interface IProviderProps {
  children: React.ReactNode
}

export const ThemeContext = createContext<IThemeContextProps>(
  {} as IThemeContextProps,
)

export const ThemeContextProvider = ({ children }: IProviderProps) => {
  // Initialize from localStorage, default to 'light'
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('theme')
    return saved === 'dark' || saved === 'light' ? saved : 'light'
  })

  // Apply theme to document and save to localStorage
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('theme', theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))
  }

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

// Custom hook for easy access
export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context.theme) {
    throw new Error('useTheme must be used within a ThemeContextProvider')
  }
  return context
}
